<script setup lang="ts">
import { computed, ref, onMounted } from 'vue'
import { useI18n } from 'vue-i18n'
import { useStore } from 'vuex'
import { useRouter } from 'vue-router'
import RenewAuthorisationDeclarationForm from '@/components/dashboard/authorisation/RenewAuthorisationDeclarationForm.vue'
import AuthExplainerModal from '@/components/dashboard/modals/AuthExplainerModal.vue'
import DashboardNavIcon from '@/components/dashboard/shell/DashboardNavIcon.vue'
import { LANDING_PAGE } from '@/types'
import { scrollWindowToTopSoon } from '@/helpers/scrollWindowToTop'
import type { Messages } from '@/i18n/en'

const store = useStore()
const router = useRouter()
const { tm } = useI18n<{ message: Messages }>()

const termsAgreed = ref(false)
const whatModalOpen = ref(false)

const whatBullets = computed(() => {
  const items = tm('LoginRegister.Declaration.WhatModal.Items')
  return Array.isArray(items) ? (items as string[]) : []
})

const onAuthorisationSuccess = async () => {
  // Mark setup complete in Vuex and stop createUser fetches before navigating —
  // otherwise mock GET /me?createUser=true can race and yank landingPage back.
  store.commit('SET_LANDING_PAGE', LANDING_PAGE.DASHBOARD)
  store.commit('SET_AUTH_INTENT', 'login')
  // Release the Agree control before replace — on mobile Safari this tap focus
  // otherwise keeps the viewport pinned mid-page across the layout swap.
  if (document.activeElement instanceof HTMLElement) {
    document.activeElement.blur()
  }
  // Prefer replace so back doesn’t return to the declaration step.
  await router.replace({ name: 'Dashboard' })
  // replace() keeps the same history entry — without a forced reset the
  // browser can leave the scrolled declaration offset on the new layout.
  scrollWindowToTopSoon()
}

onMounted(() => {
  store.commit('SET_LOADING', false)
})
</script>

<template>
  <div
    class="sa-auth-step sa-auth-step--arrive"
    data-testid="declaration-section"
  >
    <header class="sa-auth-step__header">
      <div class="sa-auth-step__icon" aria-hidden="true">
        <!-- Same shield glyph as Active Swiftaid status. -->
        <DashboardNavIcon name="matching" />
      </div>
      <h1 class="sa-auth-step__title">
        {{ $t('LoginRegister.Declaration.Header') }}
      </h1>
      <i18n-t
        keypath="LoginRegister.Declaration.SubHeader"
        tag="p"
        class="sa-auth-step__lead"
        scope="global"
      >
        <template #learnMore>
          <button
            type="button"
            class="sa-auth-step__explain"
            @click="whatModalOpen = true"
          >
            {{ $t('LoginRegister.Declaration.LearnMore') }}
          </button>
        </template>
      </i18n-t>
    </header>

    <RenewAuthorisationDeclarationForm
      v-model:terms-agreed="termsAgreed"
      terms-input-id="signup-terms"
      navigate-to-dashboard-on-success
      hide-panel-title
      @success="onAuthorisationSuccess"
    />

    <AuthExplainerModal
      :is-open="whatModalOpen"
      title-id="signup-authorise-what-title"
      :title="$t('LoginRegister.Declaration.WhatModal.Title')"
      :intro="$t('LoginRegister.Declaration.WhatModal.Intro')"
      :bullets="whatBullets"
      :footer-note="$t('LoginRegister.Declaration.WhatModal.Scope')"
      @close="whatModalOpen = false"
    />
  </div>
</template>
